import React, { useState, useEffect } from 'react';
/**
 * Module: Knowledge Base
 * Archetype: ACV
 */
import { Typography } from '@/lib/typography';
import { SHARED_UI_LABELS } from '@/config/task-display-config';
// FORCE_BUILD_CACHE_BUST: 2025-12-18-MIXED-CONTENT-FIX-V1
import { getJSON } from '@/lib/api';
import { PrimaryButton } from '@/components/shared/PrimaryButton';
import { StandardCollectionPage } from '@/components/shared/StandardCollectionPage';
import { PageShell } from '@/components/shared/PageShell';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ScorePill } from '@/components/ui/score-pill';
import { Pill } from '@/components/ui/pill';
import {
  FileText,
  BookOpen,
} from '@phosphor-icons/react';

// Governance: Read/Write Separation Components
import { kbSchema } from '@/schemas/readViewSchemas';
import { adaptKBEntry } from '@/adapters/kb.adapter';
import { normalizeEntry } from '@/lib/kb-mapper';
import { KB_MODULE_CONFIG } from '@/config/modules';
import IngestionModal from '../components/kb/IngestionModal';


import { UI_INVARIANTS } from '../config/ui-invariants';

export default function KnowledgeBase() {
  const [kbData, setKbData] = useState([]);
  const [counts, setCounts] = useState({ total: 0, accepted: 0, pending: 0 });
  const [loading, setLoading] = useState(true);

  // Stability: Consume shared invariant for default tab identity
  const [activeTab, setActiveTab] = useState(() => {
    return UI_INVARIANTS.NAVIGATION.getPersistedTab('KB') || UI_INVARIANTS.MODULES.KB.DEFAULT_TAB;
  });

  const [currentPage, setCurrentPage] = useState(1);

  // Persistence Hook: Ensure navigation re-entry respects last tab selection
  useEffect(() => {
    UI_INVARIANTS.NAVIGATION.setPersistedTab('KB', activeTab);
  }, [activeTab]);

  // --- Interaction State ---
  const [ingestOpen, setIngestOpen] = useState(false);

  useEffect(() => {
    fetchKBData();
  }, []);

  // Reset pagination on tab change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  const fetchKBData = async () => {
    setLoading(true);
    try {
      const [data, countsRes] = await Promise.all([
        getJSON('/api/kb'),
        getJSON('/api/kb/counts')
      ]);

      // Phase 4 Audit: Verify count keys match invariant IDs
      console.info('[DIAGNOSTIC] KB Counts keys:', Object.keys(countsRes));

      setKbData(data);
      setCounts(countsRes);
      console.info('[DEBUG] KB Data fetched:', data.length);
    } catch (error) {
      console.error('Failed to fetch KB:', error);
    } finally {
      setLoading(false);
    }
  };


  // --- Derived State (Controller Logic) ---
  const getFilteredData = () => {
    if (activeTab === 'analytics') return [];
    return kbData.filter(e => UI_INVARIANTS.RESOLVERS.resolveKBStatus(e.status, activeTab));
  };

  const filteredData = getFilteredData();

  // --- Governance: Stable Tab Identity (Consumed from UI_INVARIANTS) ---
  const KNOWLEDGE_TABS = UI_INVARIANTS.MODULES.KB.TABS;

  // Special Case: Analytics is NOT a Collection View
  if (activeTab === 'analytics') {
    return (
      <PageShell
        title={SHARED_UI_LABELS.knowledgeBaseTitle}
        description={SHARED_UI_LABELS.knowledgeBaseDesc}
        filterBar={
          <div className="inline-flex h-10 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground">
            {KNOWLEDGE_TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  inline-flex items-center justify-center whitespace-nowrap px-3 py-1.5 text-sm font-medium ring-offset-background transition-all rounded-lg capitalize
                  ${activeTab === tab.id
                    ? "text-white shadow-sm"
                    : "hover:bg-muted text-muted-foreground"}
                `}
                style={activeTab === tab.id ? { backgroundColor: "var(--brand-primary)" } : {}}
              >
                {tab.label}
              </button>
            ))}
          </div>
        }
      >
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Analytics & Patterns</CardTitle>
              <CardDescription>Insights from {kbData.length} entries</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-muted-foreground italic">Analytics modules temporarily unavailable.</div>
            </CardContent>
          </Card>
        </div>
      </PageShell>
    );
  }

  // Standard Collection View for Accepted/Rejected/Pending
  return (
    <>
      <StandardCollectionPage
        schema={kbSchema}
        schemaAdapter={adaptKBEntry}
        config={{
          title: SHARED_UI_LABELS.knowledgeBaseTitle,
          description: SHARED_UI_LABELS.knowledgeBaseDesc,
          entityName: "entries",
          emptyTitle: "Your Knowledge Base",
          emptyDescription: "Save articles from your inbox to build your knowledge base. Saved articles and their insights appear here.",
          emptyIcon: BookOpen,
        }}
        data={filteredData}
        status={loading ? 'loading' : 'success'}
        onRefresh={fetchKBData}

        filters={
          <div className="inline-flex h-10 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground">
            {KNOWLEDGE_TABS.map(tab => {
              const count = UI_INVARIANTS.RESOLVERS.resolveModuleCount(counts, tab.id);
              const label = tab.id === 'analytics' ? tab.label : `${tab.label} (${count})`;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    inline-flex items-center justify-center whitespace-nowrap px-3 py-1.5 text-sm font-medium ring-offset-background transition-all rounded-lg capitalize
                    ${activeTab === tab.id
                      ? "text-white shadow-sm"
                      : "hover:bg-muted text-muted-foreground"}
                  `}
                  style={activeTab === tab.id ? { backgroundColor: "var(--brand-primary)" } : {}}
                >
                  {label}
                </button>
              );
            })}
          </div>
        }

        // Actions Slot
        actions={
          <>
            <PrimaryButton onClick={() => setIngestOpen(true)}>
              <FileText className="h-4 w-4 mr-2" /> Upload
            </PrimaryButton>
          </>
        }

        // Card Render Logic
        renderCard={(rawEntry) => {
          const entry = normalizeEntry(rawEntry);
          return (
            <div className="group flex flex-col min-h-[260px] hover:shadow-lg transition-all duration-300 cursor-pointer border-border bg-card rounded-lg border h-full">
              <Card
                className="border-0 shadow-none h-full flex flex-col"
                key={entry.id}
                data-kb-entry="true"
              >
                <div className="p-6 pb-2 pr-8">
                  <h3 className={`${Typography.h3} text-foreground mb-2 line-clamp-2`}>
                    {entry.title}
                  </h3>
                  <div className="flex items-center justify-between mt-4 border-t border-border pt-4">
                    <div className="flex items-center gap-2">
                      <ScorePill score={entry.score} />
                      {entry.recommendation && (
                        <Pill variant={
                          entry.recommendation === 'SAVE' ? 'success' :
                            entry.recommendation === 'DISCARD' ? 'destructive' :
                              'secondary'
                        }>
                          {entry.recommendation}
                        </Pill>
                      )}
                    </div>

                    {entry.date && (
                      <span className="text-muted-foreground">
                        {new Date(entry.date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>

                <div className="px-6 pb-6 pt-2 flex-grow">
                  <p className="text-muted-foreground line-clamp-4 font-sans">
                    {entry.summary}
                  </p>
                </div>
              </Card>
            </div>
          );
        }}
      />

      <IngestionModal
        isOpen={ingestOpen}
        onClose={() => setIngestOpen(false)}
        onIngest={() => { fetchKBData(); setIngestOpen(false); }}
      />
    </>
  );
}
